import { useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { LEVEL_NAME, levelOf } from "../auth/levels";
import { useAccess } from "../auth/useAccess";
import type { AppConfig } from "../config";
import {
  Avatar,
  Badge,
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
  FoodicsIcon,
  Navbar,
  NavbarDivider,
  NavbarItem,
  NavbarSection,
  NavbarSpacer,
  SidebarBody,
  SidebarItem,
  SidebarLabel,
  SidebarLayout,
  SidebarPanel,
  SidebarRail,
  SidebarRailItem,
  SidebarSection,
  SidebarSpacer,
} from "../f7";
import { SECTIONS, sectionFor } from "../nav/sections";
import { useInternalLinks } from "../nav/useInternalLinks";
import { ErrorBoundary } from "./ErrorBoundary";
import { Icon, ICON_CHEVRON, ICON_SEARCH } from "./Icon";
import { ActingAs } from "./shell/ActingAs";
import { EnvBanner } from "./shell/EnvBanner";
import { Palette } from "./shell/Palette";
import { SectionHeader } from "./shell/Tabs";

/**
 * The console shell, assembled the way the design assembles it.
 *
 * A 48px RAIL carrying one icon per section, beside a 256px PANEL listing the
 * four sections with the count of tabs each holds. This is the design's own
 * structure and it is what keeps the sidebar four items long however many tabs
 * the console grows: the rail switches section, the tab strip switches subpage.
 *
 * SidebarLayout supplies the content card, its gutters and the max-w-6xl
 * measure, so nothing here adds a second wrapper — the column below owns only
 * the vertical rhythm between blocks, exactly as the design's `wrap` does.
 */
export function Layout({ config, children }: { config: AppConfig; children: ReactNode }) {
  // F7's links are plain anchors; without this every one signs the user out.
  useInternalLinks();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const section = sectionFor(pathname);
  const access = useAccess();
  const level = levelOf(access);

  /**
   * The panel collapses to just the rail. SidebarLayout draws the chevron
   * itself once it is given the state to drive — without `onToggle` there is no
   * affordance at all, which is what was missing.
   */
  const [collapsed, setCollapsed] = useState(false);

  const navbar = (
    <Navbar>
      <div className="flex min-w-0 items-center gap-2">
        {/* The design's lockup: a 3px brand-bordered tile holding the Foodics
            icon, both on the brand colour. */}
        <Link to="/" className="flex items-center gap-2 no-underline">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-xl border-[3px] border-brand-500 text-brand-500 dark:border-brand-300 dark:text-brand-300">
            <FoodicsIcon className="size-8" />
          </span>
          <span className="truncate text-sm/5 font-semibold text-slate-950 dark:text-white">
            AI Control Console
          </span>
        </Link>
      </div>

      <NavbarDivider className="max-lg:hidden" />

      {/*
        The environment, in the design's dropdown — but it does not switch.
        dev and prod are separate Cognito pools behind separate gateways, and
        `env` comes from the config.json deployed beside this bundle. A working
        switcher would have to re-authenticate against a different pool, so the
        menu says where the other one lives instead of pretending to move you.
      */}
      <Dropdown>
        <DropdownButton as={NavbarItem}>
          <span className="text-sm/5 font-medium text-slate-950 dark:text-white">
            {config.env === "prod"
              ? "Production"
              : config.env === "dev"
                ? "Development"
                : config.env}
          </span>
          <Icon paths={ICON_CHEVRON} />
        </DropdownButton>
        <DropdownMenu anchor="bottom start">
          <DropdownItem disabled>
            <DropdownLabel>
              This console is deployed per environment — each has its own sign-in and its own
              gateway. To reach another, open its own URL.
            </DropdownLabel>
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>

      <NavbarSpacer />

      <NavbarSection>
        {/* Palette owns its own open state and toggles on ⌘K, so the button
            replays that exact keystroke on `window` — the same listener, one
            code path. Lifting the state into this shell just to add a second
            way in would give the palette two owners. */}
        <NavbarItem
          aria-label="Find a tenant"
          onClick={() =>
            window.dispatchEvent(
              new KeyboardEvent("keydown", { key: "k", metaKey: true, bubbles: true }),
            )
          }
        >
          <Icon paths={ICON_SEARCH} />
        </NavbarItem>

        {/* Who you are, and what that lets you do — the design's profile
            cluster. The badge is the LEVEL, derived from the groups in the
            token, and it routes to the map that explains it. It is not a
            picker: the design offers one because a prototype has to demo every
            rung, and switching here would change no outcome the server agrees
            with. */}
        <NavbarItem
          onClick={() => navigate("/governance/permissions")}
          aria-label={`Signed in as ${access.email}. Your access.`}
        >
          <Avatar initials={initialsFor(access.email)} square className="bg-brand-800 text-white" />
          <span className="hidden text-sm/5 font-medium text-slate-950 lg:block dark:text-white">
            {nameFor(access.email)}
          </span>
          <Badge color={level === "L0" ? "amber" : "slate"}>
            {level} {LEVEL_NAME[level]}
          </Badge>
        </NavbarItem>
      </NavbarSection>
    </Navbar>
  );

  const sidebar = (
    <div className="flex h-full min-h-0">
      <SidebarRail aria-label="Sections">
        {SECTIONS.map((s) => (
          <SidebarRailItem
            key={s.id}
            label={s.label}
            current={s.id === section?.id}
            onClick={() => navigate(s.tabs[0]!.to)}
          >
            <Icon paths={s.icon} />
          </SidebarRailItem>
        ))}
      </SidebarRail>

      <SidebarPanel aria-label={section?.label ?? "Console"} className="flex">
        <SidebarBody>
          <SidebarSection>
            <p className="px-2 pt-2 pb-0.5 text-[10px] font-semibold tracking-wider text-slate-400 uppercase dark:text-slate-500">
              Console
            </p>
            {/* Nav is never hidden by authority: a page the operator cannot
                read renders its own explanation, and hiding it makes "why
                can't I see what my colleague sees?" unanswerable. */}
            {SECTIONS.map((s) => (
              <SidebarItem
                key={s.id}
                current={s.id === section?.id}
                onClick={() => navigate(s.tabs[0]!.to)}
              >
                <SidebarLabel>{s.label}</SidebarLabel>
                {s.tabs.length > 1 ? <Badge color="slate">{String(s.tabs.length)}</Badge> : null}
              </SidebarItem>
            ))}
          </SidebarSection>

          <SidebarSpacer />
          <ActingAs />
        </SidebarBody>
      </SidebarPanel>
    </div>
  );

  return (
    <>
      <SidebarLayout
        navbar={navbar}
        sidebar={sidebar}
        collapsed={collapsed}
        pinned={!collapsed}
        onToggle={() => setCollapsed((v) => !v)}
      >
        {/* The design's `wrap`: one column owning the vertical rhythm, so a
            page never sets its own top margin and no two pages disagree about
            the gap under the heading. */}
        <div className="flex flex-col gap-6">
          <EnvBanner config={config} />
          {section && <SectionHeader section={section} />}
          {/* The boundary sits INSIDE the shell and OUTSIDE the page, so a page
              that throws loses only itself — the rail, the banner and the tab
              strip stay live and you can navigate out. Keyed on the path so
              that navigation also resets it; without the key a crash would
              persist onto the next page you opened. */}
          <ErrorBoundary key={pathname} where={section?.label}>
            {children}
          </ErrorBoundary>
        </div>
      </SidebarLayout>
      {/* Its own boundary, not the page's: the page boundary is keyed on
          `pathname` and remounts on every navigation, which would silently
          reset the palette's open/closed state on every jump. A throw in here
          should still cost only the palette, not the whole console. */}
      <ErrorBoundary where="⌘K">
        <Palette config={config} />
      </ErrorBoundary>
    </>
  );
}

/**
 * Initials from the email, because Cognito carries no display name.
 *
 * `y.tekriti@…` -> YT, `youssef@…` -> Y. Never more than two letters, and
 * never a guess at which part is a surname — a wrong initial on a page that
 * stamps your name against a fleet-wide write is worse than a short one.
 */
function initialsFor(email: string): string {
  const local = email.split("@")[0] ?? "";
  const parts = local.split(/[._-]+/).filter(Boolean);
  return parts.slice(0, 2).map((p) => p[0]?.toUpperCase() ?? "").join("") || "?";
}

/**
 * The local part, title-cased. Not a claimed full name.
 *
 * The first segment is skipped when it is a single letter: `y.tekriti@` is an
 * initial and a surname, and taking the first segment rendered the whole
 * profile cluster as "Y". Prefer the first segment long enough to be a name.
 */
function nameFor(email: string): string {
  const local = email.split("@")[0] ?? "";
  const parts = local.split(/[._-]+/).filter(Boolean);
  const pick = parts.find((p) => p.length > 1) ?? parts[0] ?? local;
  return pick ? pick.charAt(0).toUpperCase() + pick.slice(1) : email;
}

/** `MCP › Merchants › 507077 › Usage` — the way back from any level-3 page. */
export function Breadcrumbs({ trail }: { trail: { label: string; to?: string }[] }) {
  return (
    <nav className="mb-3 flex flex-wrap items-center gap-1.5 text-xs/5 text-slate-500 dark:text-slate-400">
      {trail.map((c, i) => (
        <span key={i} className="flex items-center gap-1.5">
          {i > 0 && <span className="text-slate-400 dark:text-slate-500">›</span>}
          {c.to ? (
            <Link to={c.to} className="hover:underline">
              {c.label}
            </Link>
          ) : (
            <span className="text-slate-950 dark:text-white">{c.label}</span>
          )}
        </span>
      ))}
    </nav>
  );
}
